import type { SailPlotExtensionContext } from "@heiselanalytics/sailplot";
import { useEffect } from "react";
import { PaddleCheckoutButton } from "../components/PaddleCheckoutButton";
import { customizationSalesLinks } from "../config/customizationSalesLinks";
import {
  isPaddleCheckoutConfigured,
  paddleCustomerPortalUrl,
  paddlePriceIds,
} from "../integrations/paddle";
import { coreAssets } from "../tenants/coreAssets";

const copy = {
  en: {
    heroEyebrow: "Your digital sailing board",
    heroTitle: "Sail better together.",
    heroText:
      "Bring starts, mark roundings, tactics, and racing-rule situations onto one shared board. Coaches, sailors, and teams see the same picture, discuss the decisive moment, and take the lesson back onto the water. All under your own logo, colors, and URL.",
    tryConfigurator: "Try the configurator",
    example: "View customized example",
    compare: "Choose your board",
    featuresLabel: "How the customized sailing board helps",
    customizationFeatures: [
      [
        "One shared picture",
        "Place boats, marks, start lines, arrows, and notes exactly where they matter. Everyone can point to the same situation and discuss the same decisive moment.",
      ],
      [
        "Discuss. Share. Improve.",
        "Open the editable situation through a URL or QR code, continue the briefing or debrief, and turn a clear discussion into better decisions at the next start.",
      ],
      [
        "Your identity",
        "Your logo, colors, URL, language, links, and exports create one professional experience that sailors and customers immediately associate with you.",
      ],
    ],
    exampleEyebrow: "Customized example",
    exampleTitle: "Imagine your SailPlot.",
    exampleText:
      "Open a fully customized sailing board and see how its logo, colors, navigation, URL, and exports come together. Use the example to imagine how SailPlot could look and feel with your own identity.",
    exampleAction: "View customized example",
    configuratorAction: "Design your own preview",
    included: "Built for sailing",
    benefitTitle: "See clearly. Decide better.",
    benefitText:
      "SailPlot makes the decisive moment visible to the whole crew. Every plan contains the same complete editor; Customized SailPlot presents that board as your own professional coaching or organization tool.",
    benefits: [
      [
        "One shared picture",
        "Show positions, distances, courses, and alternatives on one board that everyone can discuss together.",
      ],
      [
        "Discuss and improve",
        "Make the decisive moment understandable and carry the shared conclusion into the next session or race.",
      ],
      [
        "Easy share URL",
        "A single link or QR code opens the complete editable situation for every sailor who receives it.",
      ],
      [
        "Mobile ready",
        "Use the board on desktop, tablet, or smartphone in the briefing room, clubhouse, or at the dock.",
      ],
      [
        "Made for touch",
        "Move boats and edit situations directly with touch or a mouse; the controls adapt to the screen.",
      ],
      [
        "Easy operation",
        "Create starts, mark roundings, tactical options, and rule situations with clear, focused tools.",
      ],
      [
        "Your professional board",
        "Customized SailPlot carries your logo and visual identity through the editor, URL, and exports.",
      ],
      [
        "Local and unlimited",
        "Projects stay in the browser. No account, user licenses, project caps, or export limits are required.",
      ],
    ],
    processEyebrow: "From package to launch",
    processTitle: "Live in four steps.",
    processText:
      "You define the identity. We take care of the technical implementation and let you know as soon as your board is ready.",
    processLabel: "Customized SailPlot implementation process",
    processSteps: [
      [
        "Choose your package",
        "Choose the annual branding and hosting package that fits your coaching work, club, company, or organization.",
      ],
      [
        "Design your board",
        "Use our configurator to select your logo, colors, URL, language, links, and other branding options.",
      ],
      [
        "We build it",
        "We build and check your customized SailPlot within 48 hours.",
      ],
      [
        "Ready to launch",
        "We notify you as soon as everything is live and provide the finished access to your sailing board.",
      ],
    ],
    plansEyebrow: "Choose your board",
    plansTitle: "The right board for your crew.",
    plansText:
      "The open-source sailing tools are identical in every plan. SailPlot Free is the public board; the annual packages cover your own branding, its implementation, a hosted instance, and your custom URL for twelve months.",
    offerEyebrow: "What the annual price covers",
    offerTitle: "Open source. Your brand.",
    offerText:
      "The SailPlot editor is open source and free to use. The annual price does not buy additional editor features or software access. It covers your custom branding, its implementation, a hosted SailPlot instance, your custom URL, and operation for twelve months.",
    offerAction: "View source and license",
    plansLabel: "SailPlot plans",
    bestFor: "Best for:",
    plans: [
      {
        id: "free",
        eyebrow: "Free",
        name: "SailPlot Free",
        price: "€0",
        priceSuffix: "/ forever",
        priceAction: null,
        bestFor: "Sailors and coaches who want the public sailing board",
        audience:
          "Draw, discuss, export, and share sailing situations on the public SailPlot board.",
        functionNote:
          "The complete open-source sailing board with SailPlot and Heisel Analytics branding.",
        featuresHeading: "The complete sailing board includes:",
        features: [
          "Open-source SailPlot editor under GPLv3",
          "Boats, marks, gates, course lines, arrows, shapes, text, and freehand drawing",
          "Interactive arranging and editing of every plot object",
          "Multiple local projects plus project-file import and export",
          "Unlimited PNG and PDF exports",
          "Editable share links and QR codes",
          "Desktop, tablet, and mobile use in German or English. 100% local and without an account",
        ],
        action: "Open SailPlot.app",
        href: null,
        paddlePriceId: null,
        featured: false,
      },
      {
        id: "personal-coach",
        eyebrow: "Annual branding & hosting",
        name: "Personal, Coach & Sailing Team",
        price: "€59",
        priceSuffix: "/ year",
        priceAction: null,
        bestFor: "Sailors, independent coaches, and small sailing teams",
        audience:
          "Your own branded board for personal use, coaching, and day-to-day team briefings.",
        functionNote:
          "Present every discussion on a board that sailors, customers, and teammates recognize as your professional tool.",
        featuresHeading: "Everything in SailPlot Free, plus:",
        features: [
          "Your own logo and primary color",
          "Personalized plot exports",
          "Hosted SailPlot board in your design for twelve months",
          "Dedicated custom SailPlot URL",
          "Professional presentation under one personal, coaching, or team identity",
          "Unlimited users and visitors. No user licenses",
          "Design implementation within 48 hours",
        ],
        action: "Choose this plan",
        href: customizationSalesLinks.personalCoach,
        paddlePriceId: paddlePriceIds.personalCoach,
        featured: false,
      },
      {
        id: "club-company",
        eyebrow: "Annual branding & hosting",
        name: "Club & Company",
        price: "€249",
        priceSuffix: "/ year",
        priceAction: null,
        bestFor: "Clubs, sailing schools, teams, events, and companies",
        audience:
          "One shared sailing board for coaching, education, communication, and continuous improvement.",
        functionNote:
          "Give every coach, member, employee, or customer the same professional board under your organization’s identity.",
        featuresHeading: "Everything in SailPlot Free, plus:",
        features: [
          "Club or company branding",
          "Your own logo and primary color",
          "Personalized plot exports",
          "Hosted instance and custom URL for twelve months",
          "One branded hosted instance for organization-wide use",
          "Unlimited users. No user licenses or access limits",
          "Design implementation within 48 hours",
        ],
        action: "Choose Club & Company",
        href: customizationSalesLinks.clubCompany,
        paddlePriceId: paddlePriceIds.clubCompany,
        featured: true,
      },
      {
        id: "enterprise",
        eyebrow: "Tailored branding & hosting",
        name: "Enterprise",
        price: null,
        priceSuffix: null,
        priceAction: "Contact us",
        bestFor:
          "Associations and larger organizations with individual requirements",
        audience:
          "A tailored sailing board with an individually planned identity and rollout.",
        functionNote:
          "The same complete sailing board with a professionally tailored identity, setup, and rollout for your organization.",
        featuresHeading: "Everything in SailPlot Free, plus:",
        features: [
          "Tailored branding implementation",
          "Hosted instance and custom URL for twelve months",
          "Individual requirements review",
          "Dedicated onboarding",
          "Organization-wide use with unlimited users",
          "Custom twelve-month branding, hosting, and rollout agreement",
          "Direct contact with Heisel Analytics",
          "Design implementation within 48 hours",
        ],
        action: "Contact us",
        href: customizationSalesLinks.enterprise,
        paddlePriceId: null,
        featured: false,
      },
    ],
    note: "Paid branding and hosting packages and Enterprise inquiries continue on the linked Heisel Analytics website. SailPlot.app does not process payments directly.",
    paddleNote:
      "Payments for the Personal, Coach & Sailing Team and Club & Company plans are processed through Paddle Checkout. Enterprise inquiries continue directly with Heisel Analytics.",
    checkoutDisclosure:
      "Recurring annual subscription. The card price applies to one twelve-month billing period. Paddle shows the final total, applicable taxes, billing interval, renewal information, and payment method before the binding order. Cancel before the next billing date to prevent renewal; mandatory consumer rights remain unaffected.",
    checkoutDisclosureLabel: "Annual subscription · 12 months",
    checkoutDisclosureTitle: "Billing and renewal details",
    legalLinksLabel: "Purchase and legal information",
    billing: "Manage subscription & invoices",
    terms: "Terms of service",
    refund: "Refund policy",
    privacy: "Privacy policy",
    imprint: "Legal Notice",
    finalEyebrow: "Ready for your own sailing board?",
    finalTitle: "Everyone on the same board.",
    contact: "Contact Heisel Analytics",
  },
  de: {
    heroEyebrow: "Dein digitales Segelboard",
    heroTitle: "Gemeinsam besser segeln.",
    heroText:
      "Bringe Starts, Tonnenrundungen, Taktik und Regelsituationen auf ein gemeinsames Board. Coaches, Segler und Teams sehen dasselbe Bild, besprechen den entscheidenden Moment und nehmen die Erkenntnis mit aufs Wasser – unter deinem eigenen Logo, deinen Farben und deiner URL.",
    tryConfigurator: "Konfigurator ausprobieren",
    example: "Personalisiertes Beispiel ansehen",
    compare: "Board auswählen",
    featuresLabel: "So unterstützt das personalisierte Segelboard",
    customizationFeatures: [
      [
        "Ein gemeinsames Bild",
        "Platziere Boote, Tonnen, Startlinien, Pfeile und Notizen genau dort, wo sie zählen. Alle können auf dieselbe Situation zeigen und denselben entscheidenden Moment besprechen.",
      ],
      [
        "Besprechen. Teilen. Lernen.",
        "Öffne die editierbare Situation per URL oder QR-Code, führe Briefing oder Nachbesprechung weiter und mache aus einer klaren Diskussion bessere Entscheidungen beim nächsten Start.",
      ],
      [
        "Dein Auftritt",
        "Dein Logo, deine Farben, deine URL, Sprache, Links und Exporte schaffen einen professionellen Auftritt, den Segler und Kunden sofort mit dir verbinden.",
      ],
    ],
    exampleEyebrow: "Personalisiertes Beispiel",
    exampleTitle: "Stell dir dein SailPlot vor.",
    exampleText:
      "Öffne ein vollständig personalisiertes Segelboard und sieh, wie Logo, Farben, Navigation, URL und Exporte zusammenspielen. Nutze das Beispiel als Vorschau darauf, wie SailPlot mit deinem eigenen Auftritt aussehen und wirken kann.",
    exampleAction: "Personalisiertes Beispiel ansehen",
    configuratorAction: "Eigene Vorschau gestalten",
    included: "Für Segler gemacht",
    benefitTitle: "Klar sehen. Besser entscheiden.",
    benefitText:
      "SailPlot macht den entscheidenden Moment für die ganze Crew sichtbar. Jeder Plan enthält denselben vollständigen Editor; Customized SailPlot präsentiert dieses Board als dein eigenes professionelles Coaching- oder Organisationswerkzeug.",
    benefits: [
      [
        "Ein gemeinsames Bild",
        "Zeige Positionen, Abstände, Kurse und Alternativen auf einem Board, das alle gemeinsam besprechen können.",
      ],
      [
        "Besprechen und besser werden",
        "Mache den entscheidenden Moment verständlich und nimm die gemeinsame Erkenntnis mit ins nächste Training oder in die nächste Regatta.",
      ],
      [
        "Einfach teilbare URL",
        "Ein einziger Link oder QR-Code öffnet die vollständige editierbare Situation für alle Segler, die ihn erhalten.",
      ],
      [
        "Für Mobilgeräte optimiert",
        "Nutze das Board auf Desktop, Tablet oder Smartphone im Briefingraum, Clubhaus oder am Steg.",
      ],
      [
        "Für Touch gemacht",
        "Verschiebe Boote und bearbeite Situationen direkt per Touch oder Maus; die Bedienung passt sich dem Bildschirm an.",
      ],
      [
        "Einfache Bedienung",
        "Erstelle Starts, Tonnenrundungen, taktische Optionen und Regelsituationen mit klaren, fokussierten Werkzeugen.",
      ],
      [
        "Dein professionelles Board",
        "Customized SailPlot trägt dein Logo und deinen visuellen Auftritt durch Editor, URL und Exporte.",
      ],
      [
        "Lokal und unbegrenzt",
        "Projekte bleiben im Browser. Es sind weder Konto noch Nutzerlizenzen, Projektbegrenzungen oder Exportlimits erforderlich.",
      ],
    ],
    processEyebrow: "Vom Paket zum Launch",
    processTitle: "In vier Schritten live.",
    processText:
      "Du definierst den Auftritt. Wir übernehmen die technische Umsetzung und melden uns, sobald dein Board bereit ist.",
    processLabel: "Ablauf der Customized-SailPlot-Umsetzung",
    processSteps: [
      [
        "Paket wählen",
        "Wähle das jährliche Branding- und Hosting-Paket, das zu deinem Coaching, Club, Unternehmen oder deiner Organisation passt.",
      ],
      [
        "Board gestalten",
        "Wähle in unserem Konfigurator Logo, Farben, URL, Sprache, Links und weitere Branding-Optionen aus.",
      ],
      [
        "Wir setzen es um",
        "Wir erstellen und prüfen dein Customized SailPlot innerhalb von 48 Stunden.",
      ],
      [
        "Bereit zum Start",
        "Wir benachrichtigen dich, sobald alles live ist, und übergeben dir den fertigen Zugang zu deinem Segelboard.",
      ],
    ],
    plansEyebrow: "Wähle dein Board",
    plansTitle: "Das passende Board für deine Crew.",
    plansText:
      "Die Open-Source-Segelwerkzeuge sind in jedem Plan identisch. SailPlot Free ist das öffentliche Board; die jährlichen Pakete umfassen dein eigenes Branding, dessen Umsetzung, eine gehostete Instanz und deine eigene URL für zwölf Monate.",
    offerEyebrow: "Dafür zahlst du jährlich",
    offerTitle: "Open Source. Dein Auftritt.",
    offerText:
      "Der SailPlot-Editor ist Open Source und kostenlos nutzbar. Der Jahrespreis kauft keine zusätzlichen Editor-Funktionen und keinen Softwarezugang. Er umfasst die Umsetzung deines Brandings, eine gehostete SailPlot-Instanz, deine eigene URL und den Betrieb für zwölf Monate.",
    offerAction: "Quellcode und Lizenz ansehen",
    plansLabel: "SailPlot-Pläne",
    bestFor: "Geeignet für:",
    plans: [
      {
        id: "free",
        eyebrow: "Kostenlos",
        name: "SailPlot Free",
        price: "0 €",
        priceSuffix: "/ dauerhaft",
        priceAction: null,
        bestFor:
          "Segler und Coaches, die das öffentliche Segelboard nutzen möchten",
        audience:
          "Segelsituationen auf dem öffentlichen SailPlot-Board zeichnen, besprechen, exportieren und teilen.",
        functionNote:
          "Das vollständige Open-Source-Segelboard mit SailPlot- und Heisel-Analytics-Branding.",
        featuresHeading: "Das vollständige Segelboard enthält:",
        features: [
          "Open-Source-SailPlot-Editor unter GPLv3",
          "Boote, Tonnen, Gates, Kurslinien, Pfeile, Formen, Text und Freihandzeichnungen",
          "Interaktives Anordnen und Bearbeiten aller Plot-Objekte",
          "Mehrere lokale Projekte sowie Projektdatei-Import und -Export",
          "Unbegrenzte PNG- und PDF-Exporte",
          "Editierbare Freigabelinks und QR-Codes",
          "Desktop, Tablet und Mobile auf Deutsch oder Englisch – 100 % lokal und ohne Konto",
        ],
        action: "SailPlot.app öffnen",
        href: null,
        paddlePriceId: null,
        featured: false,
      },
      {
        id: "personal-coach",
        eyebrow: "Branding & Hosting pro Jahr",
        name: "Personal, Coach & Segelteam",
        price: "59 €",
        priceSuffix: "/ Jahr",
        priceAction: null,
        bestFor: "Segler, einzelne Coaches und kleine Segelteams",
        audience:
          "Dein eigenes gebrandetes Board für persönliche Nutzung, Coaching und tägliche Teambesprechungen.",
        functionNote:
          "Führe jede Besprechung auf einem Board, das Segler, Kunden und Teammitglieder als dein professionelles Werkzeug erkennen.",
        featuresHeading: "Alles aus SailPlot Free, zusätzlich:",
        features: [
          "Eigenes Logo und eigene Primärfarbe",
          "Personalisierte Plot-Exporte",
          "Gehostetes SailPlot-Board in deinem Design für zwölf Monate",
          "Eigene SailPlot-URL",
          "Professioneller Auftritt unter einer persönlichen, Coaching- oder Teamidentität",
          "Unbegrenzt viele Nutzer und Besucher – keine Nutzerlizenzen",
          "Design-Umsetzung innerhalb von 48 Stunden",
        ],
        action: "Diesen Plan wählen",
        href: customizationSalesLinks.personalCoach,
        paddlePriceId: paddlePriceIds.personalCoach,
        featured: false,
      },
      {
        id: "club-company",
        eyebrow: "Branding & Hosting pro Jahr",
        name: "Club & Company",
        price: "249 €",
        priceSuffix: "/ Jahr",
        priceAction: null,
        bestFor: "Clubs, Segelschulen, Teams, Events und Unternehmen",
        audience:
          "Ein gemeinsames Segelboard für Coaching, Ausbildung, Kommunikation und kontinuierliche Verbesserung.",
        functionNote:
          "Gib allen Coaches, Mitgliedern, Mitarbeitenden oder Kunden dasselbe professionelle Board im Auftritt deiner Organisation.",
        featuresHeading: "Alles aus SailPlot Free, zusätzlich:",
        features: [
          "Club- oder Unternehmens-Branding",
          "Eigenes Logo und eigene Primärfarbe",
          "Personalisierte Plot-Exporte",
          "Gehostete Instanz mit eigener URL für zwölf Monate",
          "Eine gebrandete, gehostete Instanz für die organisationsweite Nutzung",
          "Unbegrenzt viele Nutzer – keine Nutzerlizenzen oder Zugriffslimits",
          "Design-Umsetzung innerhalb von 48 Stunden",
        ],
        action: "Club & Company wählen",
        href: customizationSalesLinks.clubCompany,
        paddlePriceId: paddlePriceIds.clubCompany,
        featured: true,
      },
      {
        id: "enterprise",
        eyebrow: "Individuelles Branding & Hosting",
        name: "Enterprise",
        price: null,
        priceSuffix: null,
        priceAction: "Kontakt aufnehmen",
        bestFor:
          "Verbände und grössere Organisationen mit individuellen Anforderungen",
        audience:
          "Ein massgeschneidertes Segelboard mit individuell geplantem Auftritt und Rollout.",
        functionNote:
          "Dasselbe vollständige Segelboard mit professionell abgestimmter Identität, Einrichtung und Einführung für deine Organisation.",
        featuresHeading: "Alles aus SailPlot Free, zusätzlich:",
        features: [
          "Individuelle Branding-Umsetzung",
          "Gehostete Instanz mit eigener URL für zwölf Monate",
          "Prüfung besonderer Anforderungen",
          "Persönliches Onboarding",
          "Organisationsweite Nutzung mit unbegrenzt vielen Nutzern",
          "Individuelle Vereinbarung für zwölf Monate Branding, Hosting und Einführung",
          "Direkter Kontakt mit Heisel Analytics",
          "Design-Umsetzung innerhalb von 48 Stunden",
        ],
        action: "Kontakt aufnehmen",
        href: customizationSalesLinks.enterprise,
        paddlePriceId: null,
        featured: false,
      },
    ],
    note: "Kostenpflichtige Branding- und Hosting-Pakete sowie Enterprise-Anfragen werden auf der verlinkten Website von Heisel Analytics fortgesetzt. SailPlot.app verarbeitet keine Zahlungen.",
    paddleNote:
      "Zahlungen für Personal, Coach & Segelteam sowie Club & Company werden über den Paddle Checkout abgewickelt. Enterprise-Anfragen gehen weiterhin direkt an Heisel Analytics.",
    checkoutDisclosure:
      "Wiederkehrendes Jahresabonnement. Der Kartenpreis gilt für einen Abrechnungszeitraum von zwölf Monaten. Paddle zeigt vor der verbindlichen Bestellung Gesamtpreis, anwendbare Steuern, Abrechnungsintervall, Verlängerung und Zahlungsmittel an. Kündige vor dem nächsten Abrechnungstermin, um die Verlängerung zu verhindern; zwingende Verbraucherrechte bleiben unberührt.",
    checkoutDisclosureLabel: "Jahresabonnement · 12 Monate",
    checkoutDisclosureTitle: "Details zu Abrechnung und Verlängerung",
    legalLinksLabel: "Kauf- und Rechtsinformationen",
    billing: "Abonnement & Rechnungen verwalten",
    terms: "Nutzungsbedingungen",
    refund: "Rückerstattungsrichtlinie",
    privacy: "Datenschutzerklärung",
    imprint: "Impressum",
    finalEyebrow: "Bereit für dein eigenes Segelboard?",
    finalTitle: "Alle auf demselben Board.",
    contact: "Heisel Analytics kontaktieren",
  },
} as const;

export function CustomizePage({
  language,
  navigate,
}: SailPlotExtensionContext) {
  const text = copy[language];
  const hasPaddleCheckout = text.plans.some((plan) =>
    isPaddleCheckoutConfigured(plan.paddlePriceId),
  );

  useEffect(() => {
    const isConfiguratorReturn = new URLSearchParams(
      window.location.search,
    ).has("draft_id");
    if (window.location.hash !== "#plans" && !isConfiguratorReturn) return;

    const scrollToPlans = () => {
      document.getElementById("plans")?.scrollIntoView({ block: "start" });
    };
    const frame = window.requestAnimationFrame(() => {
      scrollToPlans();
    });
    const fallback = window.setTimeout(scrollToPlans, 250);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(fallback);
    };
  }, []);

  return (
    <article className="customize-page">
      <header className="customize-hero">
        <span className="customize-eyebrow">{text.heroEyebrow}</span>
        <h1>{text.heroTitle}</h1>
        <p>{text.heroText}</p>
        <div className="customize-actions">
          <a
            className="customize-button customize-button--primary"
            href={customizationSalesLinks.configurator}
            target="_blank"
            rel="noopener noreferrer"
          >
            {text.tryConfigurator} ↗
          </a>
          <a
            className="customize-button customize-button--secondary"
            href={customizationSalesLinks.liveExample}
            target="_blank"
            rel="noopener noreferrer"
          >
            {text.example}
          </a>
          <a
            className="customize-button customize-button--secondary"
            href="#plans"
          >
            {text.compare}
          </a>
        </div>
      </header>

      <section
        className="customize-feature-grid"
        aria-label={text.featuresLabel}
      >
        {text.customizationFeatures.map(([title, description], index) => (
          <article key={title}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <h2>{title}</h2>
            <p>{description}</p>
          </article>
        ))}
      </section>

      <section className="customize-live-example">
        <div>
          <span className="customize-eyebrow">{text.exampleEyebrow}</span>
          <h2>{text.exampleTitle}</h2>
          <p>{text.exampleText}</p>
        </div>
        <div className="customize-live-example-actions">
          <a
            className="customize-button customize-button--primary"
            href={customizationSalesLinks.configurator}
            target="_blank"
            rel="noopener noreferrer"
          >
            {text.configuratorAction} ↗
          </a>
          <a
            className="customize-button customize-button--secondary"
            href={customizationSalesLinks.liveExample}
            target="_blank"
            rel="noopener noreferrer"
          >
            {text.exampleAction} ↗
          </a>
        </div>
      </section>

      <section className="customize-shared-benefits">
        <header>
          <span className="customize-eyebrow">{text.included}</span>
          <h2>{text.benefitTitle}</h2>
          <p>{text.benefitText}</p>
        </header>
        <div>
          {text.benefits.map(([title, description]) => (
            <article key={title}>
              <strong>{title}</strong>
              <span>{description}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="customize-process" aria-label={text.processLabel}>
        <header>
          <span className="customize-eyebrow">{text.processEyebrow}</span>
          <h2>{text.processTitle}</h2>
          <p>{text.processText}</p>
        </header>
        <ol>
          {text.processSteps.map(([title, description], index) => (
            <li key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{title}</h3>
              <p>{description}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="customize-section-intro" id="plans">
        <span className="customize-eyebrow">{text.plansEyebrow}</span>
        <h2>{text.plansTitle}</h2>
        <p>{text.plansText}</p>
        <div className="customize-offer-clarity">
          <div>
            <span className="customize-eyebrow">{text.offerEyebrow}</span>
            <h3>{text.offerTitle}</h3>
            <p>{text.offerText}</p>
          </div>
          <button
            type="button"
            className="customize-button customize-button--secondary"
            onClick={() => navigate("/open-source")}
          >
            {text.offerAction}
          </button>
        </div>
      </section>

      <section className="customize-plan-grid" aria-label={text.plansLabel}>
        {text.plans.map((plan) => (
          <article
            key={plan.id}
            data-plan={plan.id}
            className={`customize-plan-card${plan.featured ? " is-featured" : ""}`}
          >
            <div>
              <span className="customize-plan-label">{plan.eyebrow}</span>
              <h3>{plan.name}</h3>
              <div className="customize-plan-price">
                {plan.priceAction ? (
                  <a
                    className="customize-button customize-button--secondary"
                    href={plan.href ?? customizationSalesLinks.enterprise}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {plan.priceAction}
                  </a>
                ) : (
                  <>
                    <strong>{plan.price}</strong>
                    <span>{plan.priceSuffix}</span>
                  </>
                )}
              </div>
              <p className="customize-plan-best-for">
                <strong>{text.bestFor}</strong> {plan.bestFor}
              </p>
              <p className="customize-plan-audience">{plan.audience}</p>
              <p className="customize-plan-function-note">
                {plan.functionNote}
              </p>
            </div>
            <div className="customize-plan-includes">
              <strong>{plan.featuresHeading}</strong>
              <ul>
                {plan.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>
            {plan.paddlePriceId && (
              <details className="customize-plan-contract-note">
                <summary>
                  <span
                    className="customize-plan-contract-icon"
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 10.5v6" />
                      <path d="M12 7.5h.01" />
                    </svg>
                  </span>
                  <span>
                    <strong>{text.checkoutDisclosureLabel}</strong>
                    <small>{text.checkoutDisclosureTitle}</small>
                  </span>
                </summary>
                <p>{text.checkoutDisclosure}</p>
              </details>
            )}
            {isPaddleCheckoutConfigured(plan.paddlePriceId) ? (
              <PaddleCheckoutButton
                className={`customize-button ${plan.featured ? "customize-button--primary" : "customize-button--secondary"}`}
                label={plan.action}
                language={language}
                planId={plan.id}
                priceId={plan.paddlePriceId}
              />
            ) : plan.href ? (
              <a
                className={`customize-button ${plan.featured ? "customize-button--primary" : "customize-button--secondary"}`}
                href={plan.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {plan.action}
              </a>
            ) : (
              <button
                type="button"
                className="customize-button customize-button--secondary"
                onClick={() => navigate("/editor")}
              >
                {plan.action}
              </button>
            )}
          </article>
        ))}
      </section>

      <p className="customize-sales-note">
        {hasPaddleCheckout ? text.paddleNote : text.note}
      </p>

      <nav className="customize-legal-links" aria-label={text.legalLinksLabel}>
        <a
          href={paddleCustomerPortalUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {text.billing} ↗
        </a>
        <a href="/terms">{text.terms}</a>
        <a href="/refund-policy">{text.refund}</a>
        <a href="/privacy">{text.privacy}</a>
        <a href="/imprint">{text.imprint}</a>
      </nav>

      <section className="customize-final-cta">
        <div>
          <span className="customize-eyebrow">{text.finalEyebrow}</span>
          <h2>{text.finalTitle}</h2>
        </div>
        <a
          className="customize-button customize-button--primary"
          href={customizationSalesLinks.enterprise}
          target="_blank"
          rel="noopener noreferrer"
        >
          {text.contact}
        </a>
      </section>

      <div
        className="customize-powered-by"
        aria-label="Powered by Heisel Analytics"
      >
        <span>Powered by</span>
        <div>
          <img
            className="customize-powered-by-logo customize-powered-by-logo--on-light"
            src={coreAssets.heiselLogoLight}
            alt="Heisel Analytics"
          />
          <img
            className="customize-powered-by-logo customize-powered-by-logo--on-dark"
            src={coreAssets.heiselLogoDark}
            alt=""
          />
        </div>
      </div>
    </article>
  );
}
